import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, JsonPipe, AsyncPipe } from '@angular/common';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { Schedule, Slot, Bundle, Practitioner, Appointment, CommunicationRequest } from 'fhir/r4';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Observable, of, forkJoin, expand, reduce } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { Router } from '@angular/router';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { UtilityService } from '../shared/utility.service';
import { AuthService } from '../shared/auth/auth.service';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { ReferenceIdentifierPipe } from '../shared/reference-identifier.pipe';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { ScheduleDetailsDialogComponent } from './schedule-details-dialog.component';
import { isHospitalAdmin, normalizeRole } from '../shared/system-roles';

@Component({
    selector: 'app-schedules',
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        JsonPipe,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatChipsModule,
        MatDividerModule,
        ReferenceDisplayDirective,
        ReferenceIdentifierPipe,
        AsyncPipe,
        EmphasisDirective,
        EmptyStateComponent,
        TableHeaderComponent
    ],
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
    templateUrl: './schedules.component.html',
    styleUrls: ['../patients/patients.component.scss', './schedules.component.scss']
})
export class SchedulesComponent extends AdmittedPatientsComponent implements OnInit {

    authService = inject(AuthService);


    getLoggedInActorReference(): string {
        const user = this.authService.user.getValue();
        if (!user) return '';
        // If doctor, nurse, lab, etc. use Practitioner reference
        if (user.role && ['doctor', 'healthcareassistant', 'lab', 'pharmacy', 'remotedoctor', 'hospitaladmin', 'healthcareadmin'].includes(user.role)) {
// $#$            // /* alert('Using Practitioner reference for logged in user: ' + (user['fhirResourceId'] || user['fhirResourceId']) */);
            return `Practitioner/${user['fhirResourceId'] || user['fhirResourceId']}`;
        }
        // If patient, use Patient reference
        if (user.role === 'patient') {

            return `Patient/${user['fhirResourceId'] || user['fhirResourceId']}`;
        }
        // Fallback: empty string
        return '';
    }

    canManageSchedules(): boolean {
        const user: any = this.authService.user.getValue();
        const role = user?.role;
        return isHospitalAdmin(role) || normalizeRole(role) === 'remotedoctor' || normalizeRole(role) === 'superadmin';
    }

    private isAdminUser(): boolean {
        const user: any = this.authService.user.getValue();
        const role = user?.role;
        return isHospitalAdmin(role) || normalizeRole(role) === 'superadmin';
    }

    override ngOnInit() {
        this.displayedColumns = this.isAdminUser()
            ? ['practitioner', 'planningHorizon', 'slotCount', 'status', 'actions']
            : ['planningHorizon', 'slotCount', 'status', 'actions'];
        const queryConfig: any = {
            resourceType: 'Schedule',
            scope: 'orgWide',
            tier1FollowNextLinks: true,
            tier1MaxPages: 10,
            tier3FollowNextLinks: true,
            tier3MaxPages: 10
        };

        if (!this.isAdminUser()) {
            const actorRef = this.getLoggedInActorReference();
            queryConfig.querySearchObject = [
                {
                    key: 'actor',
                    value: [actorRef, `${this.backendUrl}/${actorRef}`]
                }
            ];
            queryConfig.urlSearchObject = {
                actor: actorRef
            };
        }


        // Check if schedules are already loaded
        // if (this.stateService.orgWideResources.schedules.value.length === 0) {
        // this.fetchSchedules();
        // } else {
        //     this.fetchSlotsForSchedules(this.stateService.orgWideResources.schedules.value.map(s => s.actualResource));
        // }

        // Subscribe to schedules AND slots to ensure updates trigger re-render
        this.initiateDataSourceSubscription(
            this.utilityService.threeTierQueryAndSyncToState$<Schedule>(queryConfig).pipe(
                switchMap((schedules) => {
                    if (!schedules || schedules.length === 0) return of(schedules);
                    return this.attachSlotCounts(schedules).pipe(
                        catchError((err) => {
                            this.errorService.openandCloseError('Failed to load slots: ' + (err.message || err));
                            return of(schedules);
                        })
                    );
                }),
                map((schedules) => {
                    return schedules;
                }),

            )
        )


        // Transform data to calculate slot counts & resolving references

    }
    utilityService = inject(UtilityService);
    fhirResourceService = inject(FhirResourceService);
    private cancellingSchedules = new Set<string>();

    fetchSchedules() {
        this.http.get<Bundle>(this.backendUrl + '/Schedule?_include=Schedule:actor:Practitioner').pipe(
            switchMap((bundle) => {

// $#$                // /* alert('Fetched ' + schedules.length + ' schedules from backend.'); */
                // if (schedules.length !== 0) {
                // return this.fetchSlotsForSchedules(bundle) as Observable<Bundle>;
                return of(bundle);
                // return of(bundle);
                // return of([] as Schedule[]);
                // } else {
                //     return of(bundle);
                // }
                // return of(schedules);
            }),
            tap((bundle) => {
                this.stateService.processOrgWideBundleTransaction(bundle);
                // const schedules = (bundle.entry || [])
                //     .map(e => e.resource)
                //     .filter(r => r?.resourceType === 'Schedule') as Schedule[];
                // return schedules;
            })
        ).subscribe({
            next: (bundle) => {
                this.stateService.processOrgWideBundleTransaction(bundle);
                const schedules = (bundle.entry || [])
                    .map(e => e.resource)
                    .filter(r => r?.resourceType === 'Schedule') as Schedule[];
// $#$                // /* alert('Fetched ' + schedules.length + ' schedules from backend.'); */

            },
            error: (err) => {
                this.errorService.openandCloseError('Failed to load schedules: ' + (err.message || err));
            }
        });
    }

    fetchSlotsForSchedules(scheduleBundle: Bundle): Observable<Bundle> {
        const schedules = (scheduleBundle.entry || [])
            .map(e => e.resource)
            .filter(r => r?.resourceType === 'Schedule') as Schedule[];
        if (!schedules || schedules.length === 0) return of(scheduleBundle);
        // Build a comma-separated list of schedule references for the Slot search
        const scheduleRefs = schedules
            .map(s => s.id ? `Schedule/${s.id}` : null)
            .filter(Boolean);
        if (scheduleRefs.length === 0) return of(scheduleBundle);

        // Use the three-tier query utility for optimized fetching and pagination
        // Assumes UtilityService is injected as this.utilityService
        return this.utilityService.threeTierQueryAndSyncToStateOnce$<Slot>({
            resourceType: 'Slot',
            urlSearchObject: { schedule: scheduleRefs.join(',') },
            // urlOrKeys: ['schedule'],
            tier1FollowNextLinks: true,

            querySearchObject: [
                { 'key': 'schedule', 'value': [...scheduleRefs, ...scheduleRefs.map(e => `${this.backendUrl}/${e}`)] }

            ],
            tier1MaxPages: 10,
            tier3FollowNextLinks: true,
            tier3MaxPages: 10,
            scope: 'orgWide',
            // count: 200
        }).pipe(map((slots) => {
// $#$            // /* alert('Fetched ' + slots.length + ' slots related to schedules from backend.'); */
            return scheduleBundle;

            // All slots are already processed into state by the utility
            // Optionally, you could do something with the slots here
        }),
            catchError((err) => {
                this.errorService.openandCloseError('Failed to load slots: ' + (err.message || err));
                return of(scheduleBundle);
            }))


        // )
        // );
    }

    getActorName(element: any): string {
        if (!element.actor || element.actor.length === 0) return 'N/A';
        const ref = element.actor[0].reference;
        // Check if we can resolve it from state? 
        // We don't have a generic "resolve reference" easily accessible here without more code, 
        // so returning reference string is acceptable for now given the task scope.
        return ref;
    }

    getPractitionerReferences(schedule: Schedule): string[] {
        const actors = Array.isArray(schedule?.actor) ? schedule.actor : [];
        const refs = actors
            .map((actor: any) => actor?.reference)
            .filter((ref: any): ref is string => typeof ref === 'string' && ref.includes('Practitioner/'))
            .map((ref: string) => this.normalizeReference(ref, 'Practitioner'))
            .filter(Boolean);

        return Array.from(new Set(refs));
    }

    private normalizeReference(reference: string, resourceType: string): string {
        const raw = (reference ?? '').toString().split('?')[0].trim();
        const marker = `${resourceType}/`;
        const markerIndex = raw.indexOf(marker);
        if (markerIndex >= 0) {
            return raw.slice(markerIndex);
        }

        const segments = raw.split('/').filter(Boolean);
        const id = segments.length ? segments[segments.length - 1] : '';
        return id ? `${resourceType}/${id}` : '';
    }

    override router = inject(Router);

    showScheduleDetails(schedule: Schedule) {
        this.dialog.open(ScheduleDetailsDialogComponent, {
            data: schedule,
            width: '940px',
            maxWidth: '95vw',
            maxHeight: '92vh'
        });
    }

    editSchedule(schedule: Schedule) {
        this.router.navigate(['/app/schedule'], { queryParams: { scheduleId: schedule.id } });
    }

    confirmCancelSchedule(schedule: Schedule): void {
        const scheduleId = schedule.id;
        if (!scheduleId || this.isCancelling(scheduleId)) {
            return;
        }

        this.setCancelling(scheduleId, true);

        this.loadCancellationContext(schedule).subscribe({
            next: (context) => {
                this.setCancelling(scheduleId, false);
                const slotCount = context.slots.length;
                const bookedCount = context.slots.filter((slot) => (slot.status ?? 'free') !== 'free').length;
                const cancellableAppointments = context.appointments.filter((appt) => this.shouldCancelAppointment(appt));

                const messages = [
                    `This schedule contains ${slotCount} slot(s) (${bookedCount} booked, ${slotCount - bookedCount} free).`,
                    cancellableAppointments.length
                        ? `${cancellableAppointments.length} related appointment(s) will be cancelled and notified.`
                        : 'No active appointments reference these slots.'
                ];

                const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    maxWidth: '460px',
                    data: {
                        title: 'Cancel schedule?',
                        messagesArray: messages,
                        confirmText: 'Cancel schedule',
                        cancelText: 'Keep schedule',
                        icon: 'event_busy',
                        iconColor: 'var(--mat-sys-error)'
                    }
                });

                dialogRef.afterClosed().subscribe((confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }
                    this.executeScheduleCancellation(schedule, context);
                });
            },
            error: () => {
                this.setCancelling(scheduleId, false);
                this.errorService.openandCloseError('Unable to load schedule details for cancellation.');
            }
        });
    }

    isCancelling(scheduleId?: string | null): boolean {
        if (!scheduleId) return false;
        return this.cancellingSchedules.has(scheduleId);
    }

    private setCancelling(scheduleId: string, active: boolean): void {
        if (!scheduleId) return;
        if (active) {
            this.cancellingSchedules.add(scheduleId);
        } else {
            this.cancellingSchedules.delete(scheduleId);
        }
    }

    private loadCancellationContext(schedule: Schedule): Observable<{ slots: Slot[]; appointments: Appointment[] }> {
        const scheduleId = schedule.id;
        if (!scheduleId) {
            return of({ slots: [], appointments: [] });
        }

        return this.fetchSlotsForSchedule(scheduleId).pipe(
            switchMap((slots) => {
                const slotIds = slots.map((slot) => slot.id).filter((id): id is string => !!id);
                if (!slotIds.length) {
                    return of({ slots, appointments: [] });
                }
                return this.fetchAppointmentsForSlots(slotIds).pipe(
                    map((appointments) => ({ slots, appointments }))
                );
            })
        );
    }

    private fetchSlotsForSchedule(scheduleId: string): Observable<Slot[]> {
        return this.fetchAllBundleResources<Slot>(
            `${this.backendUrl}/Slot?schedule=Schedule/${encodeURIComponent(scheduleId)}&_count=200`
        ).pipe(
            map((resources) => resources.filter((resource): resource is Slot => resource?.resourceType === 'Slot')),
            catchError(() => of([]))
        );
    }

    private attachSlotCounts(schedules: Schedule[]): Observable<Schedule[]> {
        const requests = schedules.map((schedule) => {
            if (!schedule.id) {
                return of({ schedule, slots: [] as Slot[] });
            }

            return this.fetchSlotsForSchedule(schedule.id).pipe(
                map((slots) => ({ schedule, slots }))
            );
        });

        return forkJoin(requests).pipe(
            map((results) => results.map(({ schedule, slots }) => ({
                ...schedule,
                totalSlots: slots.length,
                freeSlots: slots.filter((slot) => slot.status === 'free').length
            }) as Schedule))
        );
    }

    private fetchAllBundleResources<T>(initialUrl: string, maxPages = 100): Observable<T[]> {
        return this.http.get<Bundle<T>>(initialUrl).pipe(
            expand((bundle, pageIndex) => {
                if (pageIndex + 1 >= maxPages) return EMPTY;

                const nextLink = (bundle?.link ?? []).find(
                    (link) => String(link?.relation ?? '').toLowerCase() === 'next'
                )?.url;
                const nextUrl = this.normalizeFhirNextLink(nextLink);

                return nextUrl ? this.http.get<Bundle<T>>(nextUrl) : EMPTY;
            }),
            map((bundle) => (bundle?.entry ?? []).map((entry) => entry.resource as T).filter(Boolean)),
            reduce((resources, pageResources) => resources.concat(pageResources), [] as T[])
        );
    }

    private normalizeFhirNextLink(nextLink: string | undefined): string | null {
        const value = String(nextLink ?? '').trim();
        if (!value) return null;

        try {
            const nextUrl = new URL(value, `${this.backendUrl.replace(/\/$/, '')}/`);
            const slotPathIndex = nextUrl.pathname.lastIndexOf('/Slot');
            if (slotPathIndex < 0) return value;

            const backendBase = this.backendUrl.replace(/\/$/, '');
            return `${backendBase}${nextUrl.pathname.slice(slotPathIndex)}${nextUrl.search}${nextUrl.hash}`;
        } catch {
            return value;
        }
    }

    private fetchAppointmentsForSlots(slotIds: string[]): Observable<Appointment[]> {
        if (!slotIds.length) {
            return of([]);
        }

        const chunkSize = 10;
        const requests = [] as Array<Observable<Appointment[]>>;

        for (let i = 0; i < slotIds.length; i += chunkSize) {
            const chunk = slotIds.slice(i, i + chunkSize);
            const slotParams = chunk.map((id) => `slot=Slot/${encodeURIComponent(id)}`).join('&');
            const url = `${this.backendUrl}/Appointment?${slotParams}&_count=200`;
            requests.push(
                this.http.get<Bundle<Appointment>>(url).pipe(
                    map((bundle) => (bundle.entry ?? [])
                        .map((entry) => entry.resource)
                        .filter((resource): resource is Appointment => resource?.resourceType === 'Appointment')),
                    catchError(() => of([]))
                )
            );
        }

        return forkJoin(requests).pipe(map((parts) => parts.flat()));
    }

    private executeScheduleCancellation(schedule: Schedule, context: { slots: Slot[]; appointments: Appointment[] }): void {
        const scheduleId = schedule.id;
        if (!scheduleId) {
            return;
        }

        const bundle = this.buildScheduleCancellationBundle(schedule, context.slots, context.appointments);
        if (!bundle.entry || bundle.entry.length === 0) {
            this.errorService.openandCloseError('Nothing to cancel for this schedule.');
            return;
        }

        this.setCancelling(scheduleId, true);

        this.fhirResourceService.postBundle(bundle).subscribe({
            next: () => {
                this.setCancelling(scheduleId, false);
                this.errorService.openandCloseError('Schedule cancelled successfully.');
                this.fetchSchedules();
            },
            error: (err) => {
                this.setCancelling(scheduleId, false);
                this.errorService.openandCloseError('Failed to cancel schedule: ' + (err?.message || err));
            }
        });
    }

    private buildScheduleCancellationBundle(schedule: Schedule, slots: Slot[], appointments: Appointment[]): Bundle {
        const entries: NonNullable<Bundle['entry']> = [];

        if (schedule.id) {
            const updatedSchedule: Schedule = {
                ...this.sanitizeScheduleForPut(schedule),
                resourceType: 'Schedule',
                id: schedule.id,
                active: false
            };
            entries.push({
                resource: updatedSchedule,
                request: { method: 'PUT', url: `Schedule/${schedule.id}` }
            });
        }

        slots.forEach((slot) => {
            if (!slot.id) return;
            const status = (slot.status ?? '').toLowerCase() === 'free' ? 'entered-in-error' : 'busy-unavailable';
            const updatedSlot: Slot = {
                ...(slot as any),
                resourceType: 'Slot',
                id: slot.id,
                status
            };
            entries.push({
                resource: updatedSlot,
                request: { method: 'PUT', url: `Slot/${slot.id}` }
            });
        });

        const cancellableAppointments = appointments.filter((appointment) => this.shouldCancelAppointment(appointment));
        const timestamp = new Date().toISOString();

        cancellableAppointments.forEach((appointment) => {
            if (!appointment.id) return;

            const updatedAppointment: Appointment = {
                ...(appointment as any),
                resourceType: 'Appointment',
                id: appointment.id,
                status: 'cancelled',
                comment: this.appendAppointmentComment(appointment, schedule.id, timestamp)
            };

            entries.push({
                resource: updatedAppointment,
                request: { method: 'PUT', url: `Appointment/${appointment.id}` }
            });

            const recipients = (appointment.participant ?? [])
                .map((participant) => participant.actor?.reference)
                .filter((ref): ref is string => !!ref);

            if (!recipients.length) {
                return;
            }

            const communication: CommunicationRequest = {
                resourceType: 'CommunicationRequest',
                status: 'active',
                authoredOn: timestamp,
                recipient: recipients.map((ref) => ({ reference: ref })),
                payload: [{
                    contentString: `Appointment ${this.formatAppointmentSummary(appointment)} was cancelled because schedule ${schedule.id ?? ''} was removed.`
                }],
                about: appointment.id ? [{ reference: `Appointment/${appointment.id}` }] : undefined
            };

            const patientReference = this.extractPatientReferenceFromAppointment(appointment) ?? recipients[0];
            if (patientReference) {
                communication.subject = { reference: patientReference };
            }

            entries.push({
                resource: communication,
                request: { method: 'POST', url: 'CommunicationRequest' }
            });
        });

        return {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: entries
        };
    }

    private sanitizeScheduleForPut(schedule: Schedule): Schedule {
        const clone: any = { ...(schedule as any) };
        delete clone.freeSlots;
        delete clone.totalSlots;
        return clone as Schedule;
    }

    private shouldCancelAppointment(appointment: Appointment): boolean {
        const lockedStatuses = new Set(['fulfilled', 'completed', 'cancelled', 'noshow', 'entered-in-error']);
        const status = (appointment.status ?? '').toLowerCase();
        return !lockedStatuses.has(status);
    }

    private formatAppointmentSummary(appointment: Appointment): string {
        const date = appointment.start ? new Date(appointment.start) : null;
        const formatted = date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'unscheduled time';
        return appointment.id ? `${appointment.id} (${formatted})` : formatted;
    }

    private appendAppointmentComment(appointment: Appointment, scheduleId: string | undefined, timestamp: string): string {
        const base = (appointment.comment ?? '').trim();
        const note = `Cancelled due to schedule ${scheduleId ?? ''} being deactivated at ${timestamp}.`;
        return base ? `${base}\n${note}` : note;
    }

    private extractPatientReferenceFromAppointment(appointment: Appointment): string | null {
        for (const participant of appointment.participant ?? []) {
            const ref = participant.actor?.reference ?? '';
            if (ref.startsWith('Patient/')) {
                return ref;
            }
        }
        return null;
    }
}
